import { T, Var, useGT } from 'gt-react';
import type { CredentialSummaryResponse, MaskedOTPConfig } from '@erp/rpc';
import {
  REMOTE_DESKTOP_PORTAL_URLS,
  inferRemoteDesktopPortalTarget,
  type RemoteDesktopPortalTarget,
} from '@erp/shared-types';
import { Input } from 'src/components/ui/input';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  DEFAULT_REMOTE_DESKTOP_CONFIG,
  type RemoteDesktopApplicationCredentialsConfig,
  type RemoteDesktopCredentialsConfig,
} from './remote-desktop-config';
import { RemoteDesktopAppCredentialsField } from './RemoteDesktopAppCredentialsField';
import { PasswordCredentialOptions } from 'src/components/common/PasswordCredentialOptions';

export type RemoteDesktopLoginConfigFieldsProps = {
  credentials: RemoteDesktopCredentialsConfig;
  onCredentialsChange: (
    updater: (
      current: RemoteDesktopCredentialsConfig,
    ) => RemoteDesktopCredentialsConfig,
  ) => void;
  /**
   * Application credentials are consumed by the `remote_desktop_execute` step,
   * not login. Provide both this and `onApplicationCredentialsChange` to render
   * the picker (agent tools tab); omit them to hide it (workflow login node).
   */
  applicationCredentials?: RemoteDesktopApplicationCredentialsConfig;
  onApplicationCredentialsChange?: (
    updater: (
      current: RemoteDesktopApplicationCredentialsConfig,
    ) => RemoteDesktopApplicationCredentialsConfig,
  ) => void;
  passwordCredentials: CredentialSummaryResponse[];
  otpConfigs: MaskedOTPConfig[];
  /** Whether the current user can edit admin-only fields (e.g. headless). */
  showAdminFields: boolean;
  /** Suffix to keep input ids unique when multiple instances are mounted. */
  idSuffix: string;
};

/**
 * Shared remote-desktop login configuration fields. Used by the agent tools
 * tab (`login_to_remote_desktop` tool) and the matching workflow step config
 * renderer so both surfaces stay in sync.
 */
export function RemoteDesktopLoginConfigFields({
  credentials,
  applicationCredentials,
  onCredentialsChange,
  onApplicationCredentialsChange,
  passwordCredentials,
  otpConfigs,
  showAdminFields,
  idSuffix,
}: RemoteDesktopLoginConfigFieldsProps) {
  const gt = useGT();
  const portalTarget = inferRemoteDesktopPortalTarget(credentials.avdUrl);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>
          <T context="remote desktop config label for AVD portal">
            Portal Target
          </T>
        </Label>
        <Select
          value={portalTarget}
          onValueChange={(value) => {
            const nextPortalTarget: RemoteDesktopPortalTarget =
              value === 'apps' ? 'apps' : 'full_desktop';

            onCredentialsChange((current) => ({
              ...current,
              avdUrl: REMOTE_DESKTOP_PORTAL_URLS[nextPortalTarget],
            }));
          }}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={gt('Choose portal target', {
                context: 'placeholder for remote desktop portal target select',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full_desktop">
              <T context="remote desktop portal option full desktop">
                Full desktop
              </T>
            </SelectItem>
            <SelectItem value="apps">
              <T context="remote desktop portal option apps only">Apps</T>
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <T context="explains URLs for full desktop vs apps portal">
            Full desktop uses{' '}
            <Var>{REMOTE_DESKTOP_PORTAL_URLS.full_desktop}</Var>; apps uses{' '}
            <Var>{REMOTE_DESKTOP_PORTAL_URLS.apps}</Var>.
          </T>
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          <T context="label for Azure AD credential selector">
            Azure AD Credential
          </T>
        </Label>
        <Select
          value={credentials.credentialId || '__none__'}
          onValueChange={(value) =>
            onCredentialsChange((current) => ({
              ...current,
              credentialId: value === '__none__' ? '' : value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue
              placeholder={gt('Select credential...', {
                context:
                  'placeholder selecting saved password credential for Azure AD',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              <T context="option to use inline credentials instead of saved for Azure AD">
                None (use inline)
              </T>
            </SelectItem>
            <PasswordCredentialOptions credentials={passwordCredentials} />
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <T context="help text for Azure AD credential selector">
            Select a saved credential for Azure AD login (email + password).
            Manage credentials in Org Settings.
          </T>
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          <T context="label for VM password credential selector">
            VM Credential
          </T>
        </Label>
        <Select
          value={credentials.vmCredentialId || '__none__'}
          onValueChange={(value) =>
            onCredentialsChange((current) => ({
              ...current,
              vmCredentialId: value === '__none__' ? '' : value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue
              placeholder={gt('Select credential...', {
                context: 'placeholder selecting VM password credential',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              <T context="option to use inline VM password instead of saved">
                None (use inline)
              </T>
            </SelectItem>
            <PasswordCredentialOptions credentials={passwordCredentials} />
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <T context="help text for VM credential selector">
            Select a saved credential for VM login (password). Manage
            credentials in Org Settings.
          </T>
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          <T context="label for OTP MFA key selector">
            OTP Key for MFA (optional)
          </T>
        </Label>
        <Select
          value={credentials.otpTargetId || '__none__'}
          onValueChange={(value) =>
            onCredentialsChange((current) => ({
              ...current,
              otpTargetId: value === '__none__' ? '' : value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue
              placeholder={gt('None (skip MFA)', {
                context: 'placeholder when no OTP target for MFA is selected',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              <T context="option to skip MFA with no OTP key">
                None (skip MFA)
              </T>
            </SelectItem>
            {otpConfigs.map((config) => (
              <SelectItem key={config.targetId} value={config.targetId}>
                {config.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>
          {portalTarget === 'apps' ? (
            <T context="label for app name when using apps portal">App Name</T>
          ) : (
            <T context="label for desktop name when using full desktop">
              Desktop Name
            </T>
          )}
        </Label>
        <Input
          value={credentials.desktopName}
          onChange={(e) =>
            onCredentialsChange((current) => ({
              ...current,
              desktopName: e.target.value,
            }))
          }
          placeholder={
            portalTarget === 'apps'
              ? gt('SAP', {
                  context: 'example app name placeholder for apps portal',
                })
              : gt('AVD-FullDesktop', {
                  context: 'example desktop name placeholder for full desktop',
                })
          }
        />
      </div>

      <div className="space-y-2">
        <Label>
          <T context="label for optional auth state name field">
            Auth State Name (optional)
          </T>
        </Label>
        <Input
          value={credentials.authStateName}
          onChange={(e) =>
            onCredentialsChange((current) => ({
              ...current,
              authStateName: e.target.value,
            }))
          }
          placeholder={gt('Agent AVD Auth State', {
            context: 'placeholder example auth state name',
          })}
        />
      </div>

      {applicationCredentials && onApplicationCredentialsChange && (
        <RemoteDesktopAppCredentialsField
          applicationCredentials={applicationCredentials}
          onApplicationCredentialsChange={onApplicationCredentialsChange}
          passwordCredentials={passwordCredentials}
        />
      )}

      <div className="space-y-2">
        <Label>
          <T context="label for seconds to wait after desktop loads">
            Desktop Load Wait (seconds)
          </T>
        </Label>
        <Input
          type="number"
          value={credentials.desktopLoadWaitSeconds}
          onChange={(e) =>
            onCredentialsChange((current) => ({
              ...current,
              desktopLoadWaitSeconds: e.target.value
                ? Number.parseInt(e.target.value, 10)
                : DEFAULT_REMOTE_DESKTOP_CONFIG.credentials
                    .desktopLoadWaitSeconds,
            }))
          }
          min={1}
          max={120}
        />
      </div>

      {showAdminFields && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`remoteDesktopHeadless-${idSuffix}`}
            checked={credentials.headless}
            onCheckedChange={(checked) =>
              onCredentialsChange((current) => ({
                ...current,
                headless: checked === true,
              }))
            }
          />
          <Label
            htmlFor={`remoteDesktopHeadless-${idSuffix}`}
            className="cursor-pointer"
          >
            <T context="checkbox label run remote desktop headless">
              Run headless
            </T>
          </Label>
        </div>
      )}
    </div>
  );
}
